import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Layers, ShoppingBag, DollarSign, RefreshCw, Calendar, Download } from "lucide-react";
import * as XLSX from "xlsx";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { textoTotais } from "../lib/unidadeVenda";
import { CarregandoCards, CarregandoTabela } from "../components/carregando";

const API = "/api";

const COLORS = ['#FF6600', '#6366f1', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6', '#14b8a6', '#f97316', '#ec4899', '#0ea5e9', '#84cc16', '#facc15'];

const _brlFmt = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 2, maximumFractionDigits: 2 });
const _intFmt = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 0 });
const formatCurrency = (v) => _brlFmt.format(Number(v || 0));
const formatInt = (v) => _intFmt.format(Number(v || 0));
// "50,8%" (antes saía "50.8%", com ponto, direto do número do servidor).
const formatPct = (v) => `${(Number(v) || 0).toLocaleString("pt-BR", { maximumFractionDigits: 1 })}%`;
// Quilo e litro vêm à parte, com a parte quebrada (30/09/2026): "40 un · 12,350 kg".
const qtdCategoria = (c) => textoTotais({ un: c?.quantidade_itens, kg: c?.quantidade_kg, L: c?.quantidade_l });

const fmtDate = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;

const calcularDatas = (filtro, dataInicio, dataFim) => {
  const hoje = new Date();
  switch (filtro) {
    case 'hoje': return { inicio: fmtDate(hoje), fim: fmtDate(hoje) };
    case 'ontem': {
      const d = new Date(hoje); d.setDate(d.getDate() - 1);
      const iso = fmtDate(d); return { inicio: iso, fim: iso };
    }
    case '7dias': {
      const d = new Date(hoje); d.setDate(d.getDate() - 6);
      return { inicio: fmtDate(d), fim: fmtDate(hoje) };
    }
    case 'mes': {
      const inicio = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
      return { inicio: fmtDate(inicio), fim: fmtDate(hoje) };
    }
    case 'custom': return { inicio: dataInicio || fmtDate(hoje), fim: dataFim || fmtDate(hoje) };
    default: {
      const inicio = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
      return { inicio: fmtDate(inicio), fim: fmtDate(hoje) };
    }
  }
};

// === Barra de seleção de período (presets + custom com data+hora) ===
function PeriodoBar({
  filtro, setFiltro,
  dataInicio, setDataInicio, dataFim, setDataFim,
  horaInicio, setHoraInicio, horaFim, setHoraFim,
  customOpen, setCustomOpen,
}) {
  const presets = [
    { key: 'hoje', label: 'Hoje' },
    { key: 'ontem', label: 'Ontem' },
    { key: '7dias', label: '7 dias' },
    { key: 'mes', label: 'Este mês' },
    { key: 'custom', label: 'Personalizado' },
  ];
  return (
    <div className="space-y-2.5">
      <div className="flex flex-wrap items-center gap-2">
        <Calendar className="w-4 h-4 text-muted-foreground" />
        <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mr-1">Período</span>
        {presets.map(({ key, label }) => {
          const active = filtro === key;
          return (
            <button
              key={key}
              onClick={() => {
                setFiltro(key);
                if (key === 'custom') {
                  setCustomOpen(true);
                  if (!dataInicio && !dataFim) {
                    const t = fmtDate(new Date());
                    setDataInicio(t); setDataFim(t);
                    setHoraInicio('00:00'); setHoraFim('23:59');
                  }
                } else {
                  setCustomOpen(false);
                  setHoraInicio(''); setHoraFim('');
                  const { inicio, fim } = calcularDatas(key);
                  setDataInicio(inicio); setDataFim(fim);
                }
              }}
              className={`px-3 py-1 rounded-md text-xs font-medium transition-all ${
                active
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'bg-background text-muted-foreground border border-border hover:bg-accent hover:text-foreground'
              }`}
            >
              {label}
            </button>
          );
        })}
      </div>
      {filtro === 'custom' && customOpen && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-1">
          <div>
            <p className="text-xs font-medium mb-1.5 text-muted-foreground">Início</p>
            <div className="grid grid-cols-[1fr_auto] gap-2">
              <Input type="date" value={dataInicio} onChange={(e) => setDataInicio(e.target.value)} className="h-9 text-sm" />
              <Input
                type="time"
                value={horaInicio || "00:00"}
                onChange={(e) => setHoraInicio(e.target.value)}
                disabled={!dataInicio}
                className="h-9 text-sm w-[100px]"
              />
            </div>
          </div>
          <div>
            <p className="text-xs font-medium mb-1.5 text-muted-foreground">Fim</p>
            <div className="grid grid-cols-[1fr_auto] gap-2">
              <Input type="date" value={dataFim} onChange={(e) => setDataFim(e.target.value)} className="h-9 text-sm" />
              <Input
                type="time"
                value={horaFim || "23:59"}
                onChange={(e) => setHoraFim(e.target.value)}
                disabled={!dataFim}
                className="h-9 text-sm w-[100px]"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SummaryCard({ label, value, icon: Icon, color = "text-primary" }) {
  return (
    <div className="bg-card rounded-xl border p-4">
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm font-medium text-muted-foreground">{label}</p>
        <Icon className={`w-5 h-5 ${color}`} />
      </div>
      <p className="text-2xl font-bold">{value}</p>
    </div>
  );
}

export default function RelatorioCategorias() {
  const [loading, setLoading] = useState(true);
  // Carregamento com cards (25/10/2026): esqueleto só até a 1ª resposta; aplicar
  // outro período depois só esmaece os cards e a tabela.
  const [primeiraCarga, setPrimeiraCarga] = useState(true);
  const [data, setData] = useState(null);

  const [filtro, setFiltro] = useState('mes');
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');
  const [horaInicio, setHoraInicio] = useState('');
  const [horaFim, setHoraFim] = useState('');
  const [customOpen, setCustomOpen] = useState(false);

  const [appliedKey, setAppliedKey] = useState('init');

  const buildQuery = useCallback(() => {
    const { inicio, fim } = calcularDatas(filtro, dataInicio, dataFim);
    const params = new URLSearchParams({ data_inicio: inicio, data_fim: fim });
    if (filtro === 'custom') {
      if (horaInicio) params.set('hora_inicio', horaInicio);
      if (horaFim) params.set('hora_fim', horaFim);
    }
    return params.toString();
  }, [filtro, dataInicio, dataFim, horaInicio, horaFim]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const qs = buildQuery();
      const headers = { Authorization: `Bearer ${localStorage.getItem("token")}` };
      const resp = await axios.get(`${API}/relatorios/categorias?${qs}`, { headers });
      setData(resp.data);
      setAppliedKey(qs);
    } catch (err) {
      toast.error("Erro ao carregar relatório de categorias");
      setData(null);
    } finally {
      setLoading(false);
      setPrimeiraCarga(false);
    }
  }, [buildQuery]);

  // Só ao abrir: depois, quem busca é o botão "Aplicar filtro".
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchData(); }, []);

  const currentKey = buildQuery();
  const dirty = currentKey !== appliedKey;
  const handleApply = () => fetchData();

  const categorias = data?.categorias || [];
  const totais = data?.totais || { categorias: 0, itens_vendidos: 0, faturamento_total: 0 };

  const temKg = categorias.some((c) => c.quantidade_kg > 0);
  const temL = categorias.some((c) => c.quantidade_l > 0);
  const exportXlsx = () => {
    const sheet = categorias.map((c, i) => ({
      '#': i + 1,
      'Categoria': c.nome,
      'Itens vendidos (un)': c.quantidade_itens,
      ...(temKg ? { 'Quilos (kg)': c.quantidade_kg || 0 } : {}),
      ...(temL ? { 'Litros (L)': c.quantidade_l || 0 } : {}),
      'Faturamento (R$)': c.faturamento,
      '% do faturamento': formatPct(c.percentual),
    }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(sheet), 'Categorias');
    const { inicio, fim } = calcularDatas(filtro, dataInicio, dataFim);
    XLSX.writeFile(wb, `relatorio_categorias_${inicio}_${fim}.xlsx`);
  };

  return (
    <div className="p-4 sm:p-6 space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold">Relatório de Categorias</h1>
          <p className="text-muted-foreground mt-1">
            Itens vendidos, faturamento e participação de cada categoria no período
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleApply} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
          {categorias.length > 0 && (
            <Button variant="outline" size="sm" onClick={exportXlsx}>
              <Download className="w-4 h-4 mr-2" />
              Exportar
            </Button>
          )}
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-card rounded-xl border p-4 space-y-4">
        <PeriodoBar
          filtro={filtro} setFiltro={setFiltro}
          dataInicio={dataInicio} setDataInicio={setDataInicio}
          dataFim={dataFim} setDataFim={setDataFim}
          horaInicio={horaInicio} setHoraInicio={setHoraInicio}
          horaFim={horaFim} setHoraFim={setHoraFim}
          customOpen={customOpen} setCustomOpen={setCustomOpen}
        />
        <div className="flex items-center justify-end border-t pt-3">
          <Button
            variant={dirty ? 'default' : 'outline'}
            size="sm"
            onClick={handleApply}
            disabled={loading || !dirty}
            className={dirty ? 'bg-[#FF6600] hover:bg-[#e55a00] text-white' : ''}
          >
            {loading ? 'Aplicando...' : (dirty ? 'Aplicar filtro' : 'Filtro aplicado')}
          </Button>
        </div>
      </div>

      {primeiraCarga ? (
        <>
          <CarregandoCards quantos={3} className="grid grid-cols-1 sm:grid-cols-3 gap-4" altura="h-[96px]" />
          <CarregandoTabela linhas={8} colunas={6} />
        </>
      ) : (
      <div className={`space-y-6 transition-opacity duration-200 ${loading ? "opacity-50 pointer-events-none" : ""}`}>
      {/* Resumo */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <SummaryCard label="Categorias" value={formatInt(totais.categorias)} icon={Layers} color="text-[#FF6600]" />
        <SummaryCard label="Itens vendidos" value={textoTotais({ un: totais.itens_vendidos, kg: totais.kg_vendidos, L: totais.litros_vendidos })} icon={ShoppingBag} color="text-purple-500" />
        <SummaryCard label="Faturamento total" value={formatCurrency(totais.faturamento_total)} icon={DollarSign} color="text-green-500" />
      </div>

      {categorias.length === 0 ? (
        <div className="bg-card rounded-xl border p-12 text-center">
          <Layers className="w-12 h-12 mx-auto mb-3 text-muted-foreground" />
          <p className="text-muted-foreground">Nenhuma venda no período selecionado.</p>
        </div>
      ) : (
        <div className="bg-card rounded-xl border p-5 overflow-x-auto">
          <h3 className="text-base font-bold mb-4">Detalhamento por categoria</h3>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left">
                <th className="px-3 py-2 font-semibold text-muted-foreground">#</th>
                <th className="px-3 py-2 font-semibold text-muted-foreground">Categoria</th>
                <th className="px-3 py-2 font-semibold text-muted-foreground text-right">Itens vendidos</th>
                <th className="px-3 py-2 font-semibold text-muted-foreground text-right">Faturamento</th>
                <th className="px-3 py-2 font-semibold text-muted-foreground text-right">% do total</th>
                <th className="px-3 py-2 font-semibold text-muted-foreground">Participação</th>
              </tr>
            </thead>
            <tbody>
              {categorias.map((c, i) => (
                <tr key={c.nome} className={`border-b last:border-0 ${i % 2 === 1 ? 'bg-muted/30' : ''}`}>
                  <td className="px-3 py-2.5 text-muted-foreground">{i + 1}</td>
                  <td className="px-3 py-2.5 font-medium">
                    <span className="inline-flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                      {c.nome}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-right tabular-nums whitespace-nowrap">{qtdCategoria(c)}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums font-semibold text-green-600 dark:text-green-400">
                    {formatCurrency(c.faturamento)}
                  </td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{formatPct(c.percentual)}</td>
                  <td className="px-3 py-2.5">
                    <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${c.percentual}%`, backgroundColor: COLORS[i % COLORS.length] }} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      </div>
      )}
    </div>
  );
}
